import React, { useCallback, useMemo, useState } from "react";
import { useLocation, useParams } from "react-router-dom";
import MarkdownIt from "markdown-it";
import { ChevronDown } from "lucide-react";
import { useNavigation } from "../../hooks/useNavigation";
import MdEditor from "react-markdown-editor-lite";
import "react-markdown-editor-lite/lib/index.css";
import "./seo-preview.css";
import useSeoArticleApi from "../../api/use-seo-article.api";
import { AppRoute } from "../../types/routes";

// Initialize the parser (we reuse this for the copy logic)
const mdParser = new MarkdownIt({
  html: true,
  linkify: true,
  typographer: true,
  breaks: true,
});

interface LocationState {
  markdown: string;
  keyword: string;
  title?: string;
  description?: string;
  tags?: string[];
  isSaved?: boolean;
}

const SeoPreview: React.FC = () => {
  const location = useLocation();
  const { brandId } = useParams<{ brandId: string }>();
  const { replace } = useNavigation();
  const { saveSeoArticle } = useSeoArticleApi();
  const state = location.state as LocationState;
  const keyword = state?.keyword || "";

  const [markdown, setMarkdown] = useState(state?.markdown || "");
  const [copySuccess, setCopySuccess] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showMeta, setShowMeta] = useState(false);
  const metaTitle = state?.title || "";
  const metaDescription = state?.description || "";

  const metaTags = useMemo(() => {
    return state?.tags || [];
  }, [state?.tags]);

  const handleBack = useCallback(() => {
    if (!brandId) return;
    replace(AppRoute.BRAND_DETAILS.replace(":id", brandId), {
      activeTab: "growth",
      growthTab: "seo",
    });
  }, [brandId, replace]);

  // --- UPDATED SMART COPY LOGIC ---
  const handleCopy = async () => {
    try {
      // 1. Convert Markdown to HTML string
      const htmlContent = mdParser.render(markdown);

      // 2. Create Blobs for Rich Text (HTML) and Plain Text
      const blobHtml = new Blob([htmlContent], { type: "text/html" });
      const blobText = new Blob([markdown], { type: "text/plain" });

      // 3. Create a ClipboardItem with both formats
      // Browsers will intelligently choose which one to paste based on the target app
      const clipboardItem = new ClipboardItem({
        "text/html": blobHtml,
        "text/plain": blobText,
      });

      // 4. Write to system clipboard
      await navigator.clipboard.write([clipboardItem]);

      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    } catch (err) {
      console.error("Smart copy failed, trying fallback:", err);
      // Fallback for browsers (like Firefox) that might restrict ClipboardItem
      try {
        await navigator.clipboard.writeText(markdown);
        setCopySuccess(true);
        setTimeout(() => setCopySuccess(false), 2000);
      } catch (fallbackErr) {
        console.error("Copy failed entirely:", fallbackErr);
      }
    }
  };

  const handleSave = useCallback(async () => {
    if (!brandId || !keyword || !markdown.trim()) return;
    setSaving(true);
    const saved = await saveSeoArticle(brandId, keyword, markdown, {
      title: metaTitle,
      description: metaDescription,
      tags: metaTags,
    });
    setSaving(false);
    if (saved) {
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 2000);
    }
  }, [
    brandId,
    keyword,
    markdown,
    metaDescription,
    metaTags,
    metaTitle,
    saveSeoArticle,
  ]);

  if (!keyword && !markdown) {
    return (
      <div className="seo-preview-container">
        <div className="seo-preview-header">
          <button className="back-btn" onClick={handleBack}>
            ← Back
          </button>
        </div>
        <div className="seo-preview-empty">
          <p>No content to display</p>
        </div>
      </div>
    );
  }

  return (
    <div className="seo-preview-container">
      <div className="seo-preview-header">
        <button className="back-btn" onClick={handleBack}>
          ← Back
        </button>
        <div className="seo-preview-title">
          <h1>SEO Article Editor</h1>
          {keyword && <p className="seo-keyword">Keyword: {keyword}</p>}
        </div>
        <div className="header-actions">
          <button
            className={`copy-btn ${saveSuccess ? "success" : ""}`}
            onClick={handleSave}
            disabled={saving || !markdown.trim()}
            title="Save SEO article"
          >
            {saving ? "Saving..." : saveSuccess ? "Saved!" : "Save"}
          </button>
          <button
            className={`copy-btn ${copySuccess ? "success" : ""}`}
            onClick={handleCopy}
            title="Copies formatted text for Google Docs, Markdown for code editors"
          >
            {copySuccess ? (
              <>
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <polyline points="20 6 9 17 4 12"></polyline>
                </svg>
                Copied!
              </>
            ) : (
              <>
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
                  <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
                </svg>
                Smart Copy
              </>
            )}
          </button>
        </div>
      </div>
      <div className="seo-preview-main">
        <div className="seo-editor-panel">
          <div className="seo-meta-preview">
            <button
              type="button"
              className="seo-meta-preview__toggle"
              onClick={() => setShowMeta((prev) => !prev)}
            >
              <span>Metadata</span>
              <ChevronDown
                size={18}
                className={`seo-meta-preview__chevron ${
                  showMeta ? "is-open" : ""
                }`}
              />
            </button>
            {showMeta && (
              <div className="seo-meta-preview__content">
                <div className="seo-meta-preview__field">
                  <div className="seo-meta-preview__label">Title</div>
                  <input
                    className="seo-meta-preview__input"
                    readOnly
                    value={metaTitle}
                    onFocus={(event) => event.currentTarget.select()}
                  />
                </div>
                <div className="seo-meta-preview__field">
                  <div className="seo-meta-preview__label">Description</div>
                  <textarea
                    className="seo-meta-preview__textarea"
                    readOnly
                    value={metaDescription || "No description available yet."}
                    onFocus={(event) => event.currentTarget.select()}
                    rows={3}
                  />
                </div>
                <div className="seo-meta-preview__field">
                  <div className="seo-meta-preview__label">Tags</div>
                  <input
                    className="seo-meta-preview__input"
                    readOnly
                    value={
                      metaTags.length
                        ? metaTags.join(", ")
                        : "No tags available yet."
                    }
                    onFocus={(event) => event.currentTarget.select()}
                  />
                </div>
              </div>
            )}
          </div>
          <MdEditor
            value={markdown}
            style={{ height: "100%" }}
            renderHTML={(text) => mdParser.render(text)}
            onChange={({ text }) => setMarkdown(text)}
            view={{ menu: true, md: true, html: true }}
          />
        </div>
        {/* Commented out insights panel remains unchanged */}
      </div>
    </div>
  );
};

export default SeoPreview;
